<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
      @click.self="close"
    >
      <div class="bg-white rounded shadow-xl w-full max-w-xl mx-4 flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 class="font-semibold text-gray-900">Search Provider Directory</h3>
          <button class="btn-icon" @click="close">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-3">
          <!-- Search fields -->
          <form class="border border-gray-200 rounded p-3 flex flex-col gap-2 bg-gray-50" @submit.prevent="runSearch">
            <div class="grid grid-cols-2 gap-2">
              <input v-model="form.firstName" type="text" placeholder="First name" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <input v-model="form.lastName" type="text" placeholder="Last name" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <input v-model="form.organization" type="text" placeholder="Organization" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <input v-model="form.npi" type="text" placeholder="NPI" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <input v-model="form.city" type="text" placeholder="City" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              <select v-model="form.state" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40">
                <option value="">State</option>
                <option v-for="s in usStates" :key="s.code" :value="s.code">{{ s.name }}</option>
              </select>
              <input v-model="form.postal" type="text" placeholder="Zip code" class="px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
            </div>
            <p class="text-[11px] text-gray-500">
              Tip: use <span class="font-mono font-semibold">*</span> as a wildcard — e.g. <span class="font-mono">Sm*</span> matches Smith, Smyth.
            </p>
            <div>
              <button
                type="submit"
                class="btn-primary text-xs py-1 px-3"
                :disabled="loading || !hasInput"
              >
                <font-awesome-icon v-if="loading" :icon="['fas', 'circle-notch']" spin class="mr-1" />
                Search
              </button>
            </div>
          </form>

          <p v-if="error" class="text-xs text-red-600">{{ error }}</p>

          <!-- Results -->
          <ul v-if="results.length" class="divide-y divide-gray-100 border border-gray-100 rounded">
            <li
              v-for="result in results"
              :key="result.direct_address"
              class="p-2.5 flex items-start justify-between gap-2"
            >
              <div class="text-xs min-w-0">
                <div class="font-medium text-gray-800">
                  {{ displayName(result) }}
                  <span v-if="result.npi" class="font-normal text-gray-400">NPI {{ result.npi }}</span>
                </div>
                <div v-if="participantDisplayName(result)" class="text-gray-500 truncate">{{ result.direct_address }}</div>
                <div class="text-gray-500">
                  <span v-if="result.specialty">{{ result.specialty }}</span>
                  <span v-if="result.specialty && result.organization"> · </span>
                  <span v-if="result.organization">{{ result.organization }}</span>
                  <span v-if="cityState(result)"> · {{ cityState(result) }}</span>
                </div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  class="btn-secondary text-xs py-0.5 px-2"
                  :disabled="savedAddresses.has(result.direct_address)"
                  @click="addToAddressBook(result)"
                >
                  <font-awesome-icon
                    :icon="['fas', savedAddresses.has(result.direct_address) ? 'check' : 'user-plus']"
                    class="mr-1"
                  />
                  {{ savedAddresses.has(result.direct_address) ? 'Saved' : 'Add' }}
                </button>
                <button
                  v-if="selectable"
                  type="button"
                  class="btn-primary text-xs py-0.5 px-2"
                  @click="select(result)"
                >
                  Select
                </button>
              </div>
            </li>
          </ul>
          <p v-else-if="searched && !loading" class="text-xs text-gray-500 text-center py-2">No results found.</p>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex items-center justify-center gap-1 pt-1">
            <button
              type="button"
              class="btn-icon text-xs px-2 py-1"
              :disabled="loading || currentPage === 1"
              @click="goToPage(currentPage - 1)"
            >
              <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-[10px]" />
            </button>
            <button
              v-for="p in pageNumbers"
              :key="p"
              type="button"
              class="text-xs rounded px-2 py-1 min-w-7"
              :class="p === currentPage ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'"
              :disabled="loading"
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
            <button
              type="button"
              class="btn-icon text-xs px-2 py-1"
              :disabled="loading || currentPage === totalPages"
              @click="goToPage(currentPage + 1)"
            >
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-[10px]" />
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-200">
          <span v-if="totalCount" class="text-xs text-gray-500">{{ totalCount }} result{{ totalCount === 1 ? '' : 's' }}</span>
          <button class="btn-secondary ml-auto" @click="close">Close</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as messagingApi from '@/services/api/providerMessaging.js';
import { US_STATES } from '@/constants/usStates.js';
import { participantDisplayName } from '@/utils/providerMessaging.js';

export default {
  name: 'ProviderDirectorySearchModal',
  components: { FontAwesomeIcon },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Show the per-result "Select" button and emit `select` when clicked — used when this
    // modal is opened from a recipient picker rather than as a standalone directory lookup.
    selectable: {
      type: Boolean,
      default: false,
    },
    // Opens the standard search with a known NPI when compose already found
    // more than one directory match.
    initialNpi: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'select', 'address-book-updated'],

  data() {
    return {
      usStates: US_STATES,
      form: { firstName: '', lastName: '', organization: '', npi: '', city: '', state: '', postal: '' },
      results: [],
      loading: false,
      error: '',
      searched: false,
      savedAddresses: new Set(),

      // Pagination — the backend fixes page size server-side; only `page` is client-driven.
      lastQueryParams: null,
      currentPage: 1,
      totalCount: 0,
    };
  },

  computed: {
    hasInput() {
      return Object.values(this.form).some((v) => v.trim());
    },
    totalPages() {
      return this.totalCount ? Math.max(1, Math.ceil(this.totalCount / 25)) : 0;
    },
    // Windowed page numbers (max 7) centered on the current page, so a large result set
    // doesn't render hundreds of page buttons.
    pageNumbers() {
      const total = this.totalPages;
      const windowSize = 7;
      let start = Math.max(1, this.currentPage - Math.floor(windowSize / 2));
      const end = Math.min(total, start + windowSize - 1);
      start = Math.max(1, end - windowSize + 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },

  watch: {
    modelValue(val) {
      if (val) {
        this.reset();
        if (this.initialNpi.trim()) {
          this.form.npi = this.initialNpi.trim();
          this.runSearch();
        }
      }
    },
  },

  methods: {
    reset() {
      this.form = { firstName: '', lastName: '', organization: '', npi: '', city: '', state: '', postal: '' };
      this.results = [];
      this.error = '';
      this.searched = false;
      this.savedAddresses = new Set();
      this.lastQueryParams = null;
      this.currentPage = 1;
      this.totalCount = 0;
    },

    close() {
      this.$emit('update:modelValue', false);
    },

    participantDisplayName,

    displayName(result) {
      return participantDisplayName(result) || result.direct_address;
    },

    cityState(result) {
      return [result.city, result.state].filter(Boolean).join(', ');
    },

    async runSearch() {
      if (!this.hasInput) return;
      this.lastQueryParams = { ...this.form };
      await this.fetchPage(1);
    },

    async goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      await this.fetchPage(page);
    },

    async fetchPage(page) {
      this.error = '';
      this.loading = true;
      this.searched = false;
      try {
        const { total_count, results } = await messagingApi.searchDirectory({ ...this.lastQueryParams, page });
        this.results = results;
        this.totalCount = total_count;
        this.currentPage = page;
        this.searched = true;
      } catch (e) {
        this.error = messagingApi.errorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    async addToAddressBook(result) {
      try {
        await messagingApi.createContact({
          direct_address: result.direct_address,
          first_name: result.first_name ?? '',
          last_name: result.last_name ?? '',
          organization: result.organization ?? '',
          npi: result.npi ?? '',
          phone: result.phone ?? '',
          fax: result.fax ?? '',
          address1: result.address1 ?? '',
          address2: result.address2 ?? '',
          city: result.city ?? '',
          state: result.state ?? '',
          postal: result.postal ?? '',
        });
        this.savedAddresses.add(result.direct_address);
        this.$emit('address-book-updated');
      } catch (e) {
        this.error = messagingApi.errorMessage(e);
      }
    },

    select(result) {
      this.$emit('select', result);
      this.close();
    },
  },
};
</script>
